/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useDrawer } from "../drawer/DrawerProvider";
import { drawerAction } from "../../store/features/deviceSlice";
import AddChildren from "../children/AddChildren";

function capitalizeFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const handleValidity = (panel, item) => {
  if(panel==='tickets' && item?.duration) return `Validity: ${item?.duration}`
  if(panel==='packages' && item?.start_date && item?.end_date) return `Validity: ${item?.start_date} to ${item?.end_date}`
  if(panel==='memberships' && item?.duration && item?.duration_type) return `Validity: ${item?.duration} ${item?.duration_type}${item?.duration>1?'s':''}`
  
  return ''
}

export default function DetailsTicketPopup({ park, branchId }) {
  const { childId } = useSelector((state) => state.deviceSlice);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openDrawer } = useDrawer();
  const tabs = useMemo(
    () => [
      { id: 1, title: "tickets" },
      { id: 2, title: "packages" },
      { id: 3, title: "memberships" },
    ],
    []
  );

  const [activePanel, setActivePanel] = useState(tabs[0].title);
  const [tabSelectedItems, setTabSelectedItems] = useState([]);
  const [resData, setResData] = useState({});

  const handleTickets = useCallback(() => {
    if (!park) {
      setResData([]);
      return;
    }

    const RIDES_TICKETS = park?.ride_tickets?.filter((item) => item.branch_id === +branchId).map(item=>({...item, type: 'ride'}))
    const hashEntry = {
      tickets: {
        ENTRY_TICKETS: park?.entry_tickets?.filter((item) => item.branch_id === +branchId).map(item=>({...item, type: 'entry'})),
        ...(RIDES_TICKETS?.length && {RIDES_TICKETS})
      },
      packages: {
        PACKAGES: park?.packages?.filter((item) => item.branch_id === +branchId),
      },
      memberships: {
        MEMBERSHIPS: park?.memberships?.filter((item) => item.branch_id === +branchId),
      }
    }

    setResData(hashEntry[activePanel])
  }, [activePanel, park, branchId]);

  useEffect(() => {
    handleTickets();
  }, [handleTickets]);

  // Get index of current tab
  const tabIndex = tabSelectedItems.findIndex((t) => t.tab === activePanel);
  const currentItems = tabIndex !== -1 ? tabSelectedItems[tabIndex].items : [];

  // Update quantity or add/remove item in current tab array
  const updateQty = (item, delta) => {
    setTabSelectedItems((prev) => {
      const newTabs = [...prev];
      const updatedTabIndex = newTabs.findIndex((t) => t.tab === activePanel);

      const itemsInTab =
        updatedTabIndex !== -1 ? [...newTabs[updatedTabIndex].items] : [];

      const itemIndex = itemsInTab.findIndex((i) => i.id === item.id);

      if (itemIndex === -1 && delta > 0) {
        itemsInTab.push({ ...item, quantity: delta });
      } else if (itemIndex !== -1) {
        const newQty = Math.max(0, itemsInTab[itemIndex].quantity + delta);
        if (newQty === 0) {
          itemsInTab.splice(itemIndex, 1);
        } else {
          itemsInTab[itemIndex] = {
            ...itemsInTab[itemIndex],
            quantity: newQty,
          };
        }
      }

      if (updatedTabIndex !== -1) {
        if (itemsInTab.length > 0) {
          newTabs[updatedTabIndex] = { tab: activePanel, items: itemsInTab };
        } else {
          newTabs.splice(updatedTabIndex, 1); // remove if empty
        }
      } else if (itemsInTab.length > 0) {
        newTabs.push({ tab: activePanel, items: itemsInTab });
      }

      return newTabs;
    });
  };

  const allSelectedItems = tabSelectedItems.flatMap(({ items }) => items);

  // Calculate total quantity and price across all tabs
  const totalQty = allSelectedItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const totalPrice = allSelectedItems.reduce(
    (sum, item) =>
      sum + (item.offer_price ? item.offer_price : item.price) * item.quantity,
    0
  );

  function handleBuyTickets() {
    navigate("/checkout");
    dispatch(drawerAction(false));
  }

  const handleTabChange = (newTab) => {
    const hasOtherSelections = tabSelectedItems.some(
      (item) => item.tab !== newTab
    );

    if (hasOtherSelections) {
      Swal.fire({
        title: "You can only buy one ticket type per order.",
        text: "Click 'Continue Purchase' to proceed or 'Remove' to change selection.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#079d49",
        cancelButtonColor: "#d33",
        confirmButtonText: "Continue Purchase",
        cancelButtonText: "Remove",
      }).then((result) => {
        if (result.isConfirmed) {
          handleBuyTickets();
        } else if (result.isDismissed) {
          setTabSelectedItems([]);
          setActivePanel(newTab);
        }
      });
    } else {
      setActivePanel(newTab);
    }
  };

  useEffect(() => {
    if (!tabSelectedItems?.length) return;

    const formattedData = tabSelectedItems.map((tabGroup) => ({
      tab: tabGroup.tab,
      items: tabGroup.items.map((item) => ({
        ...item,
        type: activePanel,
      })),
    }));

    const hasChildrenApplicable = formattedData.some((tabGroup) =>
      tabGroup.items.some((item) => item.applicable === "children")
    );

    if (hasChildrenApplicable) {
      Swal.fire({
        text: "To complete this membership package, please add a child profile. Click 'Add Child' to proceed or 'Cancel' to go back.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#079d49",
        cancelButtonColor: "#d33",
        confirmButtonText: "Add Child",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          openDrawer(
            <AddChildren
              park={park}
              branchId={branchId}
              item={formattedData}
            />,
            "Child Details"
          );
        } else {
          setTabSelectedItems([]);
        }
      });
    }
  }, [tabSelectedItems, activePanel, childId, park, branchId, openDrawer]);

  useEffect(() => {
    sessionStorage.setItem(
      "selectedItemsByTab",
      JSON.stringify(tabSelectedItems)
    );
  }, [tabSelectedItems]);

  const replaceUnderScore = (word) => word.replace("_", " ")
  const removeS = (word) => replaceUnderScore(word).slice(0, word.length-1)
  
  return (
    <div style={{ overflow: "auto" }}>
      <div className="buy-ticket-category mb-4">
        {tabs.map(panel => <div
          key={panel.title}
          onClick={() => handleTabChange(panel.title)}
          className={`buy-ticket-category-item ${activePanel === panel.title ? 'active' : ''}`}
        >
          {capitalizeFirstLetter(panel.title)}
        </div>
        )}
      </div>

      {/* Ticket Lists */}
      {Object.entries(resData).map(([title, list]) => <div key={title} className="px-3" style={{ paddingBottom: "10px" }}>
        <h6 className="text-uppercase fw-bold small mb-3" style={{color:'orange'}}>
          {list?.length ? replaceUnderScore(title) : `No ${removeS(title)} available`}
        </h6>
        {list?.map((item) => {
          const selectedItem = currentItems.find((i) => i.id === item.id);
          const quantity = selectedItem ? selectedItem.quantity : 0;

          return (
            <div key={item.id} className="border-bottom pb-3 mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-semibold">{item.name}</div>
                  <div>
                    {item.offer_price ? (
                      <>
                        <strike className="text-success fw-bold me-2">
                          ৳ {item.price}
                        </strike>
                        <span>৳ {item.offer_price}</span>
                      </>
                    ) : (
                      <div className="text-success fw-bold">৳ {item.price}</div>
                    )}
                  </div>
                  {item.description && (
                    <div className="text-muted small">
                      {item.description}{" "}
                      {/* <span className="text-success">See More</span> */}
                    </div>
                  )}
                  <div className="small" style={{ color: "orange" }}>
                    {handleValidity(activePanel, item)}
                  </div>
                </div>
                <div className="btn-group ms-3" role="group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => updateQty(item, -1)}
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className="btn text-white"
                    style={{ backgroundColor: "#079d49" }}
                  >
                    {quantity}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => updateQty(item, 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Bottom Fixed Cart */}
      <div
        className="bg-white shadow-lg rounded p-3 pt-2 d-flex flex-column w-100"
        style={{ position: "absolute", bottom: 0, zIndex: 10 }}
      >
        <div>
          <div className="d-flex justify-content-between mb-2">
            <span className="fw-semibold">{totalQty} ticket(s) selected</span>
            <h5 className="fw-bold text-success m-0">৳ {totalPrice}</h5>
          </div>
          <button
            type="button"
            className="btn text-white px-4 fw-semibold w-100"
            onClick={handleBuyTickets}
            style={{ backgroundColor: "#079d49" }}
            disabled={totalQty === 0}
          >
            BUY TICKETS
          </button>
        </div>
      </div>
    </div>
  );
}
